// Worker status — one function, one cell.
//
// This used to be two implementations over the same rows: a snapshot-shaped one
// here for the Workers table and a host-shaped one in pages/cluster for the
// Upstream pool, kept in step by a test that pinned both halves. They disagreed
// anyway — only the pool side honoured `desiredState`, and only this side split
// "the box is gone" from "local-api is gone". `workerStatus` keeps both
// distinctions.

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import { formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import type { ContainerLifecycle, WorkerRow } from "@/lib/workers/types";

export type WorkerStatusTone =
	| "ok"
	| "starting"
	| "api-dead"
	| "host-down"
	| "missing"
	| "crashed"
	| "stopped"
	| "unknown";

/** The reachability fields `workerStatus` reads, so a timeline row can pass a
 *  bare snapshot without inventing a whole worker. */
export interface WorkerStatusInput {
	desiredState?: WorkerRow["desiredState"];
	container?: ContainerLifecycle | null;
	snapshot: {
		reachable: boolean;
		apiVersion: string | null;
		collectedAt: string;
		unreachableReason: "api-dead" | "host-down" | null;
	} | null;
}

/**
 * A failed probe means five different things and the operator needs them apart.
 *
 * - A worker the operator stopped is "stopped", not "unreachable": the poller
 *   skips it, so its last snapshot is stale by design. Intent wins over
 *   everything, including a probe that still succeeds against a container on its
 *   way down.
 * - A container docker still reports as `health: starting` fails its probe for
 *   the first half-minute of every boot. Painting that red sends someone to
 *   debug a worker that is merely not up yet.
 * - A container that no longer exists is the opposite: no amount of waiting
 *   fixes it.
 * - A container that exited while it was meant to be running has crashed.
 *   Reporting that as "host down" points at the network when docker already
 *   knows the process is gone.
 * - Everything else keeps the host-down / API-offline split, which is the
 *   difference between an unreachable box and a box whose local-api died.
 *
 * A reachable worker is reported as reachable regardless of what docker thinks:
 * if it is serving, it is serving.
 */
export function workerStatus(row: WorkerStatusInput): WorkerStatusTone {
	if (row.desiredState === "stopped") return "stopped";
	if (row.snapshot?.reachable) return "ok";
	if (row.container === "starting") return "starting";
	if (row.container === "missing") return "missing";
	if (row.container === "stopped") return "crashed";
	if (!row.snapshot) return "unknown";
	return row.snapshot.unreachableReason ?? "host-down";
}

/**
 * The label at rest. A healthy worker reads as its API version — on a table of
 * workers, "reachable" repeated N times says less than the number an operator is
 * actually checking for drift.
 */
export function workerStatusLabel(row: WorkerStatusInput): string {
	const tone = workerStatus(row);
	switch (tone) {
		case "ok":
			return row.snapshot?.apiVersion ?? "reachable";
		case "starting":
			return "starting";
		case "api-dead":
			return "API offline";
		case "host-down":
			return "host down";
		case "missing":
			return "container missing";
		case "crashed":
			return "container exited";
		case "stopped":
			return "stopped";
		case "unknown":
			return "no data";
	}
}

/** Worst-first, so a sort on this column puts problems where they can be seen. */
const TONE_RANK: Record<WorkerStatusTone, number> = {
	missing: 0,
	crashed: 1,
	"host-down": 2,
	"api-dead": 3,
	unknown: 4,
	stopped: 5,
	starting: 6,
	ok: 7,
};

export function workerStatusRank(row: WorkerStatusInput): number {
	return TONE_RANK[workerStatus(row)];
}

function toneDotClass(tone: WorkerStatusTone): string {
	switch (tone) {
		case "ok":
			return "bg-emerald-500";
		case "api-dead":
			return "bg-amber-500";
		case "host-down":
		case "missing":
		case "crashed":
			return "bg-destructive";
		case "starting":
			return "bg-muted-foreground/70";
		case "unknown":
		case "stopped":
			return "bg-muted-foreground/40";
	}
}

function toneLabelClass(tone: WorkerStatusTone): string {
	switch (tone) {
		case "ok":
			return "text-foreground";
		case "api-dead":
			return "text-amber-700 dark:text-amber-400";
		case "host-down":
		case "missing":
		case "crashed":
			return "text-destructive";
		case "starting":
		case "unknown":
		case "stopped":
			return "text-muted-foreground";
	}
}

function toneDetail(
	tone: WorkerStatusTone,
	collected: string | undefined,
): string {
	switch (tone) {
		case "stopped":
			return "Container is stopped. Start it to resume polling and LB routing.";
		case "starting":
			return "Container is up and still completing its health check. It answers once local-api has finished booting, usually within a minute of a fresh deploy.";
		case "missing":
			return "The container behind this worker no longer exists. Remove the worker, or deploy it again under the same name to reuse its install volume.";
		case "crashed":
			return "The container exited while it was meant to be running. Check its logs, then start it again from this page.";
		case "api-dead":
			return "The box answers but local-api does not. Check the container's logs and the API key on this worker.";
		default:
			return collected
				? `Collected ${formatRelative(collected)} (${collected})`
				: "Polling hasn't reported a status for this host.";
	}
}

/**
 * Status cell. The dot and a short readable label are visible at rest; the hover
 * detail keeps the precise collected-at timestamp.
 *
 * `starting` earns the one piece of motion on this surface. It is the only state
 * that is going to resolve on its own, and a slow pulse says "wait" in a way a
 * static grey dot cannot. The global `prefers-reduced-motion` override zeroes it,
 * and the label carries the meaning either way.
 */
export function WorkerStatusCell({ row }: { row: WorkerStatusInput }) {
	const tone = workerStatus(row);
	const label = workerStatusLabel(row);
	const collected = row.snapshot?.collectedAt;
	const detail = toneDetail(tone, collected);
	const showCollected = collected && tone !== "stopped" && tone !== "starting";

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="inline-flex min-w-0 cursor-default items-center gap-2 text-xs">
					<span
						className="relative inline-flex size-1.5 shrink-0"
						aria-hidden="true"
					>
						{tone === "starting" ? (
							<span className="absolute inset-0 animate-ping rounded-full bg-muted-foreground/70" />
						) : null}
						<span
							className={cn(
								"relative inline-flex size-1.5 rounded-full",
								toneDotClass(tone),
							)}
						/>
					</span>
					<span
						className={cn(
							"truncate font-mono tabular-nums",
							toneLabelClass(tone),
						)}
					>
						{label}
					</span>
					{showCollected ? (
						<span className="hidden truncate text-muted-foreground sm:inline">
							· {formatRelative(collected)}
						</span>
					) : null}
				</span>
			</TooltipTrigger>
			<TooltipContent side="right">
				<span className="font-medium">{label}</span>
				<span className="block text-muted-foreground">{detail}</span>
			</TooltipContent>
		</Tooltip>
	);
}
